'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["strategy"];
import * as DSAssetFamilies from 'customer-data-filters/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import * as Operators from 'customer-data-filters/filterQueryFormat/operator/Operators';
import * as ReferenceObjectTypes from 'reference-resolvers/constants/ReferenceObjectTypes';
import { DSAssetFamilyToObjectType } from 'customer-data-filters/filterQueryFormat/DSAssetFamilies/DSAssetFamilyMappings';
import { Map as ImmutableMap, OrderedMap } from 'immutable';
import { NONE } from 'customer-data-filters/filterQueryFormat/timestamps/TimestampTypes';
import { CustomObjectTypeIdRegex, ObjectTypeIdRegex } from 'customer-data-filters/filterQueryFormat/ObjectTypeId';
import { StringDisplayType } from 'customer-data-filters/filterQueryFormat/DisplayTypes';
import { getUniqueFieldIdsByAssetFamily } from 'customer-data-filters/filterQueryFormat/FilterQueryFormat';
import { parseDynamicFilterFamilyId, testDynamicFilterFamily } from '../filterQueryFormat/DynamicFilterFamilies';
import { hasSiteContentAccess } from '../permissions/scopes';
import { isFilterValueHasRestrictedProperties } from '../permissions/fieldLevelPermissions';
import And from 'customer-data-filters/filterQueryFormat/logic/And';
import ConnectReferenceResolvers from 'reference-resolvers/ConnectReferenceResolvers';
import Constraint from 'customer-data-filters/filterQueryFormat/logic/Constraint';
import FieldResolverNull from 'customer-data-filters/filterQueryFormat/knownOptionResolvers/FieldResolverNull';
import FilterStrategyType from './propTypes/FilterStrategyType';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ImmutablePropTypes from 'react-immutable-proptypes';
import ListSegClassicFilterFormatType from './propTypes/ListSegClassicFilterFormatType';
import ListSegClassicStrategy from './strategies/ListSegClassicStrategy';
import MissingField from 'customer-data-filters/filterQueryFormat/MissingField';
import ObjectSegFilterFormatType from './propTypes/ObjectSegFilterFormatType';
import Or from 'customer-data-filters/filterQueryFormat/logic/Or';
import PropTypes from 'prop-types';
import PropertyNameToReferenceType from 'customer-data-objects/property/PropertyNameToReferenceType';
import { PureComponent } from 'react';
import ReferenceResolverType from 'reference-resolvers/schema/ReferenceResolverType';
import XOFilterEditor from 'customer-data-filters/components/XOFilterEditor';
import getIn from 'transmute/getIn';
import omit from 'transmute/omit';
import AllObjectPropertiesReferenceResolver from 'reference-resolvers/resolvers/AllObjectPropertiesReferenceResolver';
import { makeAtom } from 'atom';
import FilterUserAction from '../filterQueryFormat/FilterUserAction';
import { VIEWED_FILTERS } from '../filterQueryFormat/UsageSubActionTypes';
import CRMSearchFilterFormatType from './propTypes/CRMSearchFilterFormatType';
import * as FiltersReferenceObjectTypes from '../data/FiltersReferenceObjectTypes';
import { AppObjectTypeIdRegex } from '../filterQueryFormat/ObjectTypeId';
import { jsx as _jsx } from "react/jsx-runtime";
const dynamicResolvers = {};
class DSFilterEditor extends PureComponent {
  constructor() {
    super();
    this.getField = keyPath => {
      const [assetFamily, id] = keyPath;
      const {
        data,
        hasScope,
        strategy
      } = this.props;
      const defaultField = MissingField({
        name: `${id}`,
        timestampType: NONE,
        type: StringDisplayType,
        userHasNoAccess: !strategy.getHasFilterFamilyAccess(assetFamily, hasScope),
        errorMessage: data.get(assetFamily) ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataFilters.MissingField.fetchFailed"
        }) : undefined
      });
      const fieldFromStrategy = strategy.getField(assetFamily, id, defaultField, this.props);
      const field = fieldFromStrategy || data.getIn([assetFamily, String(id)]);
      return field || defaultField;
    };
    this.getFieldGroups = assetFamily => {
      const {
        strategy
      } = this.props;
      return strategy.getFieldGroups(assetFamily, this.props);
    };
    this.getFields = assetFamily => {
      const {
        strategy
      } = this.props;
      return strategy.getFields(assetFamily, this.props);
    };
    this.getFilterFamilyAndButtonTooltip = filterFamily => {
      const {
        strategy
      } = this.props;
      return strategy.getFilterFamilyAndButtonTooltip(filterFamily, this.props);
    };
    this.getFilterFamilyGroupHeading = filterFamily => {
      const {
        strategy
      } = this.props;
      return strategy.getFilterFamilyGroupHeading(filterFamily, this.props);
    };
    this.getFilterFamilyHasEntities = filterFamily => {
      if (testDynamicFilterFamily(DSAssetFamilies.UNIFIED_EVENT, filterFamily)) {
        return true;
      }
      if (filterFamily === DSAssetFamilies.CUSTOM_BEHAVIORAL_EVENT) {
        return true;
      }
      return false;
    };
    this.getFilterFamilyName = filterFamily => {
      const {
        strategy
      } = this.props;
      return strategy.getFilterFamilyName(filterFamily, this.props);
    };
    this.getFilterFamilyObjectName = (filterFamily, isPlural = false) => {
      const {
        strategy,
        data
      } = this.props;
      return strategy.getFilterFamilyObjectName(filterFamily, isPlural, data);
    };
    this.getFilterFamilyOptions = () => {
      const {
        strategy
      } = this.props;
      return strategy.getFilterFamilyOptions(this.props);
    };
    this.getObjectAssociationOptions = assetFamily => {
      const {
        strategy,
        baseFilterFamily,
        data
      } = this.props;
      return strategy.getObjectAssociationOptions(assetFamily, baseFilterFamily, data);
    };
    this.getObjectPropertyLabel = (assetFamily, fieldName) => {
      const {
        data
      } = this.props;
      return data.getIn([assetFamily, fieldName, 'label']) || fieldName;
    };
    this.getReferenceResolvers = filterFamily => {
      const {
        hasScope,
        strategy,
        resolvers
      } = this.props;
      const meetingOutcomeResolver = resolvers.get(ReferenceObjectTypes.MEETING_OUTCOME);
      let baseResolvers = ImmutableMap({
        ACTIVITY_TYPE: resolvers.get(ReferenceObjectTypes.ACTIVITY_TYPE),
        AUTOMATION_PLATFORM_WORKFLOWS: resolvers.get(ReferenceObjectTypes.AUTOMATION_PLATFORM_WORKFLOWS),
        CALL_DISPOSITION: resolvers.get(ReferenceObjectTypes.CALL_DISPOSITION),
        MEETING_OUTCOME: meetingOutcomeResolver,
        DEAL_PIPELINE: resolvers.get(ReferenceObjectTypes.DEAL_PIPELINE),
        DEAL_STAGE: resolvers.get(ReferenceObjectTypes.DEAL_PIPELINE_STAGE),
        OWNER: resolvers.get(ReferenceObjectTypes.OWNER),
        PERSONA: resolvers.get(ReferenceObjectTypes.PERSONA),
        SALESFORCE_CAMPAIGN: resolvers.get(ReferenceObjectTypes.SALESFORCE_CAMPAIGN),
        TEAM: resolvers.get(ReferenceObjectTypes.HIERARCHICAL_TEAM),
        USER: resolvers.get(ReferenceObjectTypes.USER),
        [ReferenceObjectTypes.EMAIL_CAMPAIGN_URL]: resolvers.get(ReferenceObjectTypes.EMAIL_CAMPAIGN_URL),
        [DSAssetFamilies.ADS_PROPERTY]: resolvers.get(ReferenceObjectTypes.ADS_PROPERTY),
        [DSAssetFamilies.COMPANY_PROPERTY]: resolvers.get(ReferenceObjectTypes.COMPANY_PROPERTY),
        [DSAssetFamilies.CONTACT_PROPERTY]: resolvers.get(ReferenceObjectTypes.CONTACT_PROPERTY),
        [DSAssetFamilies.CTA]: resolvers.get(ReferenceObjectTypes.CTA),
        [DSAssetFamilies.DEAL_PROPERTY]: resolvers.get(ReferenceObjectTypes.DEAL_PROPERTY),
        [DSAssetFamilies.EMAIL_SUBSCRIPTION]: resolvers.get(this.props.getIsUngated('subscriptions:groupSettings') ? ReferenceObjectTypes.EMAIL_SUBSCRIPTION_GROUPS : ReferenceObjectTypes.EMAIL_SUBSCRIPTION),
        [DSAssetFamilies.ENGAGEMENT_PROPERTY]: resolvers.get(ReferenceObjectTypes.ENGAGEMENT_PROPERTY),
        [DSAssetFamilies.LINE_ITEM_PROPERTY]: resolvers.get(ReferenceObjectTypes.LINE_ITEM_PROPERTY),
        [DSAssetFamilies.PRODUCT_PROPERTY]: resolvers.get(ReferenceObjectTypes.PRODUCT_PROPERTY),
        [ReferenceObjectTypes.ADS_FACEBOOK_AD]: resolvers.get(ReferenceObjectTypes.ADS_FACEBOOK_AD),
        [ReferenceObjectTypes.ADS_FACEBOOK_ADGROUP]: resolvers.get(ReferenceObjectTypes.ADS_FACEBOOK_ADGROUP),
        [ReferenceObjectTypes.ADS_FACEBOOK_CAMPAIGN]: resolvers.get(ReferenceObjectTypes.ADS_FACEBOOK_CAMPAIGN),
        [ReferenceObjectTypes.ADS_GOOGLE_AD]: resolvers.get(ReferenceObjectTypes.ADS_GOOGLE_AD),
        [ReferenceObjectTypes.ADS_GOOGLE_ADGROUP]: resolvers.get(ReferenceObjectTypes.ADS_GOOGLE_ADGROUP),
        [ReferenceObjectTypes.ADS_GOOGLE_CAMPAIGN]: resolvers.get(ReferenceObjectTypes.ADS_GOOGLE_CAMPAIGN),
        [ReferenceObjectTypes.ADS_GOOGLE_KEYWORD]: resolvers.get(ReferenceObjectTypes.ADS_GOOGLE_KEYWORD),
        [ReferenceObjectTypes.ADS_LINKEDIN_AD]: resolvers.get(ReferenceObjectTypes.ADS_LINKEDIN_AD),
        [ReferenceObjectTypes.ADS_LINKEDIN_ADGROUP]: resolvers.get(ReferenceObjectTypes.ADS_LINKEDIN_ADGROUP),
        [ReferenceObjectTypes.ADS_LINKEDIN_CAMPAIGN]: resolvers.get(ReferenceObjectTypes.ADS_LINKEDIN_CAMPAIGN),
        [ReferenceObjectTypes.COMPANY]: resolvers.get(ReferenceObjectTypes.COMPANY),
        [ReferenceObjectTypes.MULTI_CURRENCY_CURRENCY_CODE]: resolvers.get(ReferenceObjectTypes.MULTI_CURRENCY_CURRENCY_CODE),
        // HACK: at least one scope related to content access is required to
        // use the values inside FormSubmissions operators but not the whole
        // FormSubmissions family, see LIST-1143.
        [ReferenceObjectTypes.SITE_CONTENT]: hasSiteContentAccess(hasScope) ? resolvers.get(ReferenceObjectTypes.SITE_CONTENT) : FieldResolverNull,
        [ReferenceObjectTypes.TICKET_PIPELINE]: resolvers.get(ReferenceObjectTypes.TICKET_PIPELINE),
        [ReferenceObjectTypes.TICKET_STAGE]: resolvers.get(ReferenceObjectTypes.TICKET_STAGE),
        [FiltersReferenceObjectTypes.MARKETING_EVENT_COORDINATES]: resolvers.get(FiltersReferenceObjectTypes.MARKETING_EVENT_COORDINATES),
        [ReferenceObjectTypes.MEDIA_BRIDGE_MEDIA_OBJECT]: resolvers.get(ReferenceObjectTypes.MEDIA_BRIDGE_MEDIA_OBJECT),
        [FiltersReferenceObjectTypes.CAMPAIGN_COORDINATES]: resolvers.get(FiltersReferenceObjectTypes.CAMPAIGN_COORDINATES),
        [FiltersReferenceObjectTypes.WEB_INTERACTIVE_COORDINATES]: resolvers.get(FiltersReferenceObjectTypes.WEB_INTERACTIVE_COORDINATES),
        [ReferenceObjectTypes.SOCIAL_CHANNEL]: resolvers.get(ReferenceObjectTypes.SOCIAL_CHANNEL)
      });

      // add dynamic reference resolver for custom + app objects
      // (see https://git.hubteam.com/HubSpot/customer-data-filters/pull/928)
      if (CustomObjectTypeIdRegex.test(filterFamily) || AppObjectTypeIdRegex.test(filterFamily)) {
        let coResolver = dynamicResolvers[filterFamily];
        if (!coResolver) {
          coResolver = AllObjectPropertiesReferenceResolver(filterFamily, {})(() => makeAtom());
          dynamicResolvers[filterFamily] = coResolver;
        }
        baseResolvers = baseResolvers.set(filterFamily, coResolver);
      }
      const strategyResolvers = strategy.getReferenceResolvers(resolvers);
      return baseResolvers.merge(strategyResolvers);
    };
    this.getReferenceType = (filterFamily, name) => {
      const propertyObjectType = DSAssetFamilyToObjectType.get(filterFamily) || filterFamily;
      return getIn([propertyObjectType, name], PropertyNameToReferenceType);
    };
    this.getReferenceTypeForOperator = (operator, assetFamily) => {
      switch (operator.constructor) {
        case Operators.After:
        case Operators.Before:
        case Operators.UpdatedAfter:
        case Operators.UpdatedBefore:
          return assetFamily;
        case Operators.CtaHasClicked:
        case Operators.CtaHasNotClicked:
        case Operators.CtaHasNotSeen:
        case Operators.CtaHasSeen:
          return DSAssetFamilies.CTA;
        case Operators.EmailLinkClicked:
          return ReferenceObjectTypes.EMAIL_CAMPAIGN_URL;
        case Operators.EmailSubscriptionNotOpted:
        case Operators.EmailSubscriptionOptIn:
        case Operators.EmailSubscriptionOptOut:
          return DSAssetFamilies.EMAIL_SUBSCRIPTION;
        case Operators.FormFilledOut:
        case Operators.FormNotFilledOut:
          return ReferenceObjectTypes.SITE_CONTENT;
        default:
          return null;
      }
    };
    this.getValue = () => {
      const {
        baseFilterFamily,
        constraint,
        isAndGroup,
        strategy,
        value,
        getIsUngated
      } = this.props;
      const serializerOptions = strategy.getSerializerOptions();
      const filterValue = strategy.serializer.fromFormat(value, baseFilterFamily, this.getField, getIsUngated, Object.assign({}, serializerOptions, {
        constraint
      }));
      if (isAndGroup) {
        if (filterValue.conditions.size === 0) {
          return And();
        }
        return filterValue.conditions.first();
      }
      return filterValue;
    };
    this.handleChange = (filter, handler) => {
      const {
        constraint,
        enableAdvancedOnChangeValue,
        isAndGroup,
        strategy,
        getIsUngated
      } = this.props;
      if (filter && typeof handler === 'function') {
        const nextFilter = isAndGroup ? Or.of(filter) : filter;
        const serializerOptions = strategy.getSerializerOptions();
        const filterQuery = strategy.serializer.toFormat(nextFilter, getIsUngated, this.props.data, Object.assign({}, serializerOptions, {
          constraint
        }));
        const payload = enableAdvancedOnChangeValue === true ? {
          serialized: filterQuery,
          filterQueryFormat: nextFilter
        } : filterQuery;
        handler(payload);
      }
    };
    this.handleChangeFilter = filter => {
      const {
        onChange
      } = this.props;
      return this.handleChange(filter, onChange);
    };
    this.handleChangeDraft = filter => {
      const {
        onDraftChange
      } = this.props;
      return this.handleChange(filter, onDraftChange);
    };
    this.handleLoadMoreFields = (assetFamily, newOffset, searchQuery) => {
      const {
        data,
        fetchMore
      } = this.props;
      if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, assetFamily)) {
        const integrationId = parseDynamicFilterFamilyId(DSAssetFamilies.INTEGRATION, assetFamily);
        const integrationFields = data.getIn([DSAssetFamilies.INTEGRATION_PROPERTY, integrationId]);
        if (!integrationFields) {
          fetchMore({
            variables: {
              assetFamily: DSAssetFamilies.INTEGRATION_PROPERTY,
              ids: [integrationId]
            }
          });
        }
        return;
      }

      // Fetch required fields for groups.
      if (assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION && !data.has(DSAssetFamilies.SURVEY_MONKEY_SURVEY)) {
        fetchMore({
          variables: {
            assetFamily: DSAssetFamilies.SURVEY_MONKEY_SURVEY
          }
        });
      } else if (ObjectTypeIdRegex.test(assetFamily) && !data.hasIn([DSAssetFamilies.PROPERTY_GROUP, assetFamily])) {
        fetchMore({
          variables: {
            assetFamily: DSAssetFamilies.PROPERTY_GROUP,
            ids: [assetFamily]
          }
        });
      }
      if (assetFamily === DSAssetFamilies.CUSTOM_BEHAVIORAL_EVENT) {
        fetchMore({
          variables: {
            assetFamily: DSAssetFamilies.CUSTOM_BEHAVIORAL_EVENT
          }
        });
      }
      if (assetFamily !== DSAssetFamilies.PAGE_VIEW && assetFamily !== DSAssetFamilies.EMAIL_SUBSCRIPTION && !testDynamicFilterFamily(DSAssetFamilies.UNIFIED_EVENT, assetFamily)) {
        fetchMore({
          variables: {
            assetFamily,
            query: searchQuery
          }
        });
      }
    };
    this.validateOperator = operator => {
      const {
        strategy
      } = this.props;
      return strategy.validateOperator(operator);
    };
    this.state = {
      integrations: OrderedMap()
    };
  }
  componentDidMount() {
    const {
      fetchMore,
      strategy,
      value: filterValue,
      onUserAction
    } = this.props;
    if (typeof onUserAction === 'function') {
      onUserAction(FilterUserAction({
        subAction: VIEWED_FILTERS
      }));
    }
    if (isFilterValueHasRestrictedProperties(filterValue)) {
      return;
    }
    const requiredIdsByAssetFamily = getUniqueFieldIdsByAssetFamily(this.getValue());

    // fetch required fields for required assetFamilies
    requiredIdsByAssetFamily.delete(DSAssetFamilies.INTEGRATION).filter(idSet => idSet.size).forEach((idSet, assetFamily) => {
      fetchMore({
        variables: {
          assetFamily,
          ids: idSet.toArray()
        }
      });
    });
    strategy.onMount(this.props, requiredIdsByAssetFamily);
  }
  componentDidUpdate(prevProps) {
    const {
      strategy
    } = this.props;
    strategy.onUpdate(prevProps, this.props);
  }
  render() {
    const _this$props = this.props,
      {
        strategy
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const value = this.getValue();
    const filterConditionCount = value.conditions.size || 0;
    return /*#__PURE__*/_jsx(XOFilterEditor, Object.assign({}, rest, {
      getFieldDefinitions: strategy.getFieldDefinitions,
      getFieldGroups: this.getFieldGroups,
      getFields: this.getFields,
      getFilterFamilyAndButtonTooltip: this.getFilterFamilyAndButtonTooltip,
      getFilterFamilyGroupHeading: this.getFilterFamilyGroupHeading,
      getFilterFamilyHasEntities: this.getFilterFamilyHasEntities,
      getFilterFamilyName: this.getFilterFamilyName,
      getFilterFamilyObjectName: this.getFilterFamilyObjectName,
      getFilterFamilyOptions: this.getFilterFamilyOptions,
      getObjectAssociationOptions: this.getObjectAssociationOptions,
      getObjectPropertyLabel: this.getObjectPropertyLabel,
      getOperatorsForType: strategy.getOperatorsForType,
      getReferenceResolvers: this.getReferenceResolvers,
      getReferenceType: this.getReferenceType,
      getReferenceTypeForOperator: this.getReferenceTypeForOperator,
      getSpecialFieldOptions: strategy.getSpecialFieldOptions,
      isFieldDescriptionVisible: true,
      isInitialScreenCreate: filterConditionCount === 0,
      isXoEnabled: true,
      loadMoreFields: this.handleLoadMoreFields,
      onChange: this.handleChangeFilter,
      onDraftChange: this.handleChangeDraft,
      validateOperator: this.validateOperator,
      value: value
    }));
  }
}
DSFilterEditor.defaultProps = {
  baseFilterFamily: DSAssetFamilies.CONTACT_PROPERTY,
  strategy: ListSegClassicStrategy
};
DSFilterEditor.propTypes = {
  baseFilterFamily: PropTypes.string.isRequired,
  constraint: PropTypes.instanceOf(Constraint),
  data: ImmutablePropTypes.map.isRequired,
  enableAdvancedOnChangeValue: PropTypes.bool,
  fetchMore: PropTypes.func.isRequired,
  getActiveFieldNames: PropTypes.func,
  getIsUngated: PropTypes.func.isRequired,
  hasScope: PropTypes.func.isRequired,
  isAndGroup: PropTypes.bool,
  onChange: PropTypes.func,
  onDraftChange: PropTypes.func,
  onUserAction: PropTypes.func,
  resolvers: ImmutablePropTypes.mapOf(ReferenceResolverType),
  strategy: FilterStrategyType.isRequired,
  value: PropTypes.oneOfType([ListSegClassicFilterFormatType, ObjectSegFilterFormatType, CRMSearchFilterFormatType])
};

// These are resolver factories and require extra args.
const DSFILTER_UNSUPPORTED_RESOLVERS = [ReferenceObjectTypes.ALL_OBJECT_PROPERTIES, ReferenceObjectTypes.AUTOMATED_EMAIL, ReferenceObjectTypes.CUSTOM_OBJECT, ReferenceObjectTypes.OBJECT_PROPERTIES, ReferenceObjectTypes.INBOUND_DB_IMPORT];
const mapResolversToProps = resolvers => ({
  resolvers: ImmutableMap(omit(DSFILTER_UNSUPPORTED_RESOLVERS, resolvers))
});
export default ConnectReferenceResolvers(mapResolversToProps, DSFilterEditor);